// ============================================================================
// TENANT IMPACT SETTINGS VIEW (BLOCK 4)
// Configures tenant-level impact metrics assumptions,
// SLA / stuck queue settings, and business hours for SLA clock.
//
// Access: tenant_admin (read/write own), ops_manager (read-only own), platform_admin (cross-tenant)
// Firestore: tenants/{tenantId}/settings/impact
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  Settings, Save, RefreshCcw, AlertCircle, CheckCircle, Loader2,
  Building, Clock, Calculator, ChevronDown, Info, Shield, Calendar, Plug, ArrowRight
} from 'lucide-react';
import {
  getTenants,
  getImpactSettings,
  saveImpactSettings,
  isPlatformAdmin,
  isTenantAdmin,
  isOpsManager,
  DEFAULT_IMPACT_SETTINGS,
  getSchedulingAvailability,
  saveSchedulingAvailability,
  validateSchedulingAvailability,
} from '../core/index.js';
import { useAuth } from '../auth/AuthContext.jsx';

// 30-minute time options ("HH:MM" 24h) for Scheduling Availability start/end.
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  const hh = String(h).padStart(2, '0');
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return { value: `${hh}:${m}`, label: `${h12}:${m} ${ampm}` };
});

// ============================================================================
// CONSTANTS
// ============================================================================

const SLA_ROWS = [
  { key: 'UNCLAIMED_NEW',     type: 'state', label: 'Unclaimed New',          desc: 'New referral not yet claimed by intake (status = NEW with no owner assigned)' },
  { key: 'NEEDS_INFO',        type: 'state', label: 'Needs Info',             desc: 'Waiting for required information to continue processing' },
  { key: 'triageUrgent',      type: 'triage', field: 'urgentBusinessHours',      label: 'Triage — Urgent',        desc: 'High/Urgent priority referrals — nuclear override on breach', borderClass: 'border-red-500/30', textClass: 'text-red-300' },
  { key: 'triageRoutine',     type: 'triage', field: 'routineBusinessHours',     label: 'Triage — Routine',       desc: 'Standard priority referrals' },
  { key: 'triageHold',        type: 'triage', field: 'clinicalHoldBusinessHours', label: 'Triage — Clinical Hold', desc: 'Referrals placed on hold by triage nurse', borderClass: 'border-purple-500/30', textClass: 'text-purple-300' },
  { key: 'schedulingWindow',  type: 'scheduling', field: 'businessHours', label: 'Scheduling Window', desc: 'Time to schedule after triage approval — clock starts at triageCompletedAt' },
  { key: 'SCHEDULED',         type: 'state', label: 'Scheduled',              desc: 'Measures how long patients wait for their scheduled appointment' },
  { key: 'SEEN',              type: 'state', label: 'Seen',                   desc: 'Patient seen, awaiting closed loop' },
];

const US_TIMEZONES = [
  { value: 'America/New_York',    label: 'Eastern (ET)' },
  { value: 'America/Chicago',     label: 'Central (CT)' },
  { value: 'America/Denver',      label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage',   label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii (HST)' },
];

const WEEKDAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const ampm = i < 12 ? 'AM' : 'PM';
  const h = i === 0 ? 12 : i > 12 ? i - 12 : i;
  return { value: i, label: `${h}:00 ${ampm}` };
});

const END_HOURS = Array.from({ length: 24 }, (_, i) => {
  const v = i + 1;
  const ampm = v < 12 || v === 24 ? 'AM' : 'PM';
  const h = v === 24 ? 12 : v === 12 ? 12 : v > 12 ? v - 12 : v;
  const suffix = v === 24 ? 'AM (midnight)' : `${ampm}`;
  return { value: v, label: `${h}:00 ${suffix}` };
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================

// Guided-learning focus highlight — same convention as the dashboard/OpsHome.
// Returns a leading-space class string (or '') to append to className.
const focusHighlight = (target, activeFocusTarget) =>
  (target && activeFocusTarget === target ? ' ring-2 ring-cyan-400 animate-pulse rounded-xl' : '');

// ============================================================================
// SCHEDULING AVAILABILITY SECTION — booking-constraint editor
//
// Self-contained: owns its own load/edit/save against
// tenants/{tenantId}/settings/schedulingAvailability. Conceptually separate from
// Business Hours for SLA Clock (which governs SLA timing only). workingDays are
// numeric 0–6; endTime is exclusive. "Copy from Business Hours" is an explicit
// admin action that pre-fills the form (the admin still reviews + saves).
// ============================================================================
function SchedulingAvailabilitySection({ tenantId, readOnly, currentUserUid, businessHours, activeFocusTarget = null }) {
  const [state,   setState]   = useState('loading'); // loading | ready | missing | error
  const [exists,  setExists]  = useState(false);
  const [form,    setForm]    = useState({ timezone: 'America/New_York', workingDays: [1, 2, 3, 4, 5], startTime: '08:00', endTime: '16:30' });
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [success, setSuccess] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) { setState('error'); return; }
    setState('loading'); setError(null); setSuccess(null);
    try {
      const { exists: e, availability } = await getSchedulingAvailability(tenantId);
      if (e && availability) {
        setForm({
          timezone:    availability.timezone || 'America/New_York',
          workingDays: Array.isArray(availability.workingDays) ? [...availability.workingDays].sort((a, b) => a - b) : [],
          startTime:   availability.startTime || '08:00',
          endTime:     availability.endTime || '16:30',
        });
        setExists(true); setState('ready');
      } else {
        setExists(false); setState('missing');
      }
    } catch {
      setState('error');
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const toggleDay = (d) => setForm((f) => ({
    ...f,
    workingDays: f.workingDays.includes(d)
      ? f.workingDays.filter((x) => x !== d)
      : [...f.workingDays, d].sort((a, b) => a - b),
  }));

  const copyFromBusinessHours = () => {
    if (!businessHours) return;
    const pad = (n) => String(n).padStart(2, '0');
    setForm({
      timezone:    businessHours.timezone || 'America/New_York',
      workingDays: Array.isArray(businessHours.workingDays) ? [...businessHours.workingDays].sort((a, b) => a - b) : [1, 2, 3, 4, 5],
      startTime:   `${pad(businessHours.startHour ?? 8)}:00`,
      endTime:     `${pad(businessHours.endHour ?? 17)}:00`,
    });
    setSuccess(null);
    setError('Copied from Business Hours. Review and Save to apply.');
  };

  const handleSave = async () => {
    setError(null); setSuccess(null);
    const { valid, errors } = validateSchedulingAvailability(form);
    if (!valid) { setError(errors.join(' ')); return; }
    setSaving(true);
    try {
      await saveSchedulingAvailability(tenantId, { ...form, updatedByUid: currentUserUid });
      setExists(true); setSuccess('Scheduling availability saved.');
    } catch (e) {
      setError(safeUserError(e));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-violet-500/50 disabled:opacity-50';

  return (
    <div data-learning-target="impact_scheduling_availability" className={`bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden${focusHighlight('impact_scheduling_availability', activeFocusTarget)}`}>
      <div className="p-4 border-b border-slate-700/50 bg-slate-800/40">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-violet-400" />
          <h2 className="text-lg font-semibold text-white">Scheduling Availability</h2>
        </div>
        <p className="text-xs text-slate-400 mt-1">
          Business Hours define when operational delay is counted. Scheduling Availability defines when appointments can be booked.
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Most clinics should start by copying Business Hours here, then adjust if appointment booking has a narrower schedule.
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Setup guidance banner */}
        <div className="flex items-start gap-2 p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
          <Info className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-semibold text-violet-300">Start from Business Hours</p>
            <p className="text-xs text-slate-400 mt-0.5">
              Set your SLA Business Hours first, then copy them into Scheduling Availability if appointment booking should follow the same days and hours. You can still adjust Scheduling Availability separately.
            </p>
          </div>
        </div>

        {state === 'loading' && (
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {state === 'error' && (
          <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <span className="text-xs text-red-300">Could not load scheduling availability.</span>
            <button onClick={load} className="text-xs text-red-200 underline underline-offset-2">Retry</button>
          </div>
        )}

        {(state === 'ready' || state === 'missing') && (
          <>
            {!exists && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-300">
                  Not configured yet. Scheduling is blocked until this is saved. Set the working days and hours below, or copy them from Business Hours, then Save.
                </p>
              </div>
            )}

            {/* Timezone */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Timezone</label>
              <div className="relative inline-block">
                <select
                  value={form.timezone}
                  onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
                  disabled={saving || readOnly}
                  className={`w-72 ${inputCls}`}
                >
                  {US_TIMEZONES.map((tz) => (<option key={tz.value} value={tz.value}>{tz.label}</option>))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
              <p className="text-xs text-slate-500 mt-1">Used to decide which weekday an appointment date falls on.</p>
            </div>

            {/* Working days */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Available Days</label>
              <div className="flex gap-2">
                {WEEKDAYS.map(({ value, label }) => {
                  const active = form.workingDays.includes(value);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => !readOnly && !saving && toggleDay(value)}
                      disabled={saving || readOnly}
                      className={`w-12 h-10 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                        active
                          ? 'bg-violet-500/20 text-violet-300 border border-violet-500/40'
                          : 'bg-slate-900/40 text-slate-500 border border-slate-700/50 hover:border-slate-600'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Start / End time */}
            <div className="grid grid-cols-2 gap-6 max-w-md">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Start Time</label>
                <div className="relative">
                  <select
                    value={form.startTime}
                    onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
                    disabled={saving || readOnly}
                    className={`w-full ${inputCls}`}
                  >
                    {TIME_OPTIONS.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">End Time</label>
                <div className="relative">
                  <select
                    value={form.endTime}
                    onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
                    disabled={saving || readOnly}
                    className={`w-full ${inputCls}`}
                  >
                    {TIME_OPTIONS.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
                <p className="text-xs text-slate-500 mt-1">End time is exclusive — the last bookable slot is before this time.</p>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-300">{error}</p>
              </div>
            )}
            {success && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-emerald-400">{success}</p>
              </div>
            )}

            {!readOnly && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save Availability</>}
                </button>
                <button
                  type="button"
                  onClick={copyFromBusinessHours}
                  disabled={saving || !businessHours}
                  className="px-4 py-2.5 border border-slate-700 hover:border-slate-600 text-slate-300 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Copy from Business Hours
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const TenantImpactSettingsView = ({ activeFocusTarget = null, onNavigate = null }) => {
  const { user } = useAuth();

  // Guided learning: scroll the active focus target into view when it changes.
  // Highlight/pulse is applied via className on each target element. Missing
  // targets are a safe no-op (no crash, no console error).
  useEffect(() => {
    if (!activeFocusTarget || typeof document === 'undefined') return;
    const el = document.querySelector(`[data-learning-target="${activeFocusTarget}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }, [activeFocusTarget]);
  const userIsPlatformAdmin = isPlatformAdmin(user?.role);
  const userIsTenantAdmin = isTenantAdmin(user?.role);
  const userIsOpsManager = isOpsManager(user?.role);
  const readOnly = userIsOpsManager;
  const showTenantPicker = userIsPlatformAdmin;

  // State
  const [tenants, setTenants] = useState({});
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);

  // Load tenants on mount (platform_admin only) or auto-resolve tenantId
  useEffect(() => {
    if (showTenantPicker) {
      const loadTenants = async () => {
        try {
          const tenantsData = await getTenants();
          setTenants(tenantsData || {});
        } catch (err) {
          console.error('Error loading tenants:', err);
          setError('Failed to load tenants');
        } finally {
          setLoading(false);
        }
      };
      loadTenants();
    } else if (user?.tenantId) {
      setSelectedTenantId(user.tenantId);
      setLoading(false);
    } else {
      setError('No tenant assigned to your account');
      setLoading(false);
    }
  }, [showTenantPicker, user?.tenantId]);

  // Load settings when tenant changes
  const loadSettings = useCallback(async () => {
    if (!selectedTenantId) return;

    setLoadingSettings(true);
    setError('');
    setSuccess('');
    setValidationErrors([]);

    try {
      const impactSettings = await getImpactSettings(selectedTenantId);
      const defaults = DEFAULT_IMPACT_SETTINGS;

      const mergedSettings = {
        assumptions: {
          ...defaults.assumptions,
          ...(impactSettings?.assumptions || {}),
        },
        definitions: {
          ...defaults.definitions,
          ...(impactSettings?.definitions || {}),
        },
        segmentation: {
          ...defaults.segmentation,
          ...(impactSettings?.segmentation || {}),
        },
        sla: {
          states: {
            ...defaults.sla.states,
            ...(impactSettings?.sla?.states || {}),
          },
          thresholds: {
            ...defaults.sla.thresholds,
            ...(impactSettings?.sla?.thresholds || {}),
          },
          triage: {
            ...defaults.sla.triage,
            ...(impactSettings?.sla?.triage || {}),
          },
          scheduling: {
            ...defaults.sla.scheduling,
            ...(impactSettings?.sla?.scheduling || {}),
          },
        },
        businessHours: {
          ...defaults.businessHours,
          ...(impactSettings?.businessHours || {}),
        },
      };

      setSettings(mergedSettings);
      setOriginalSettings(JSON.parse(JSON.stringify(mergedSettings)));
    } catch (err) {
      console.error('Error loading impact settings:', err);
      setError('Failed to load impact settings');
      const defaults = DEFAULT_IMPACT_SETTINGS;
      setSettings(JSON.parse(JSON.stringify(defaults)));
      setOriginalSettings(JSON.parse(JSON.stringify(defaults)));
    } finally {
      setLoadingSettings(false);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const hasChanges = settings && originalSettings &&
    JSON.stringify(settings) !== JSON.stringify(originalSettings);

  // ---- Client-side validation ----
  const validateSettings = (s) => {
    const errs = [];
    const t = s.sla?.thresholds;
    if (t) {
      if (t.warnPercent < 0.05 || t.warnPercent > 0.20) {
        errs.push('Warn threshold must be between 5% and 20%.');
      }
      if (t.criticalPercent < 0.15 || t.criticalPercent > 0.40) {
        errs.push("You can't set Critical above 40%. At that level, the system is already considered operationally broken.");
      }
      if (t.warnPercent >= t.criticalPercent) {
        errs.push('Warn threshold must be lower than Critical threshold.');
      }
    }
    if (s.sla?.states) {
      for (const [state, cfg] of Object.entries(s.sla.states)) {
        if (typeof cfg.slaHours !== 'number' || cfg.slaHours < 1) {
          const rowMatch = SLA_ROWS.find(r => r.type === 'state' && r.key === state);
          errs.push(`SLA hours for ${rowMatch?.label || state} must be at least 1.`);
        }
      }
    }
    if (s.sla?.triage) {
      const tr = s.sla.triage;
      if (typeof tr.urgentBusinessHours === 'number' && tr.urgentBusinessHours < 1) errs.push('Urgent triage SLA must be at least 1 business hour.');
      if (typeof tr.routineBusinessHours === 'number' && tr.routineBusinessHours < 1) errs.push('Routine triage SLA must be at least 1 business hour.');
      if (typeof tr.clinicalHoldBusinessHours === 'number' && tr.clinicalHoldBusinessHours < 1) errs.push('Clinical hold SLA must be at least 1 business hour.');
    }
    if (s.sla?.scheduling) {
      if (typeof s.sla.scheduling.businessHours === 'number' && s.sla.scheduling.businessHours < 1) errs.push('Scheduling SLA must be at least 1 business hour.');
    }
    const bh = s.businessHours;
    if (bh) {
      if (!bh.timezone) errs.push('Business hours timezone is required.');
      if (!Array.isArray(bh.workingDays) || bh.workingDays.length === 0) {
        errs.push('At least one working day must be selected.');
      }
      if (bh.startHour >= bh.endHour) {
        errs.push('Start hour must be before end hour.');
      }
    }
    return errs;
  };

  // ---- Save handler ----
  const handleSave = async () => {
    if (!selectedTenantId || !settings || !user?.uid) {
      setError('Missing required data to save');
      return;
    }

    const errs = validateSettings(settings);
    if (errs.length > 0) {
      setValidationErrors(errs);
      return;
    }
    setValidationErrors([]);

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const slaWithSource = {
        ...settings.sla,
        thresholds: {
          ...settings.sla.thresholds,
          source: 'tenant',
        },
      };

      const flatSettings = {
        minutesSavedPerPreventedReferral: settings.assumptions.minutesSavedPerPreventedReferral,
        minutesSpentPerIncompleteReferral: settings.assumptions.minutesSpentPerIncompleteReferral,
        completedOutcomeStatuses: settings.definitions.completedOutcomeStatuses,
        preventedOutcomeStatuses: settings.definitions.preventedOutcomeStatuses,
        sla: slaWithSource,
        businessHours: settings.businessHours,
      };

      await saveImpactSettings(selectedTenantId, user.uid, flatSettings);
      setOriginalSettings(JSON.parse(JSON.stringify(settings)));
      setSuccess('Impact settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving impact settings:', err);
      setError(safeUserError(err));
    } finally {
      setSaving(false);
    }
  };

  // ---- Immutable update helpers ----
  const updateAssumption = (field, value) => {
    setSettings(prev => ({
      ...prev,
      assumptions: { ...prev.assumptions, [field]: value },
    }));
  };

  const updateSlaHours = (state, hours) => {
    setSettings(prev => ({
      ...prev,
      sla: {
        ...prev.sla,
        states: {
          ...prev.sla.states,
          [state]: { slaHours: hours },
        },
      },
    }));
  };

  const updateSlaThreshold = (field, value) => {
    setSettings(prev => ({
      ...prev,
      sla: {
        ...prev.sla,
        thresholds: { ...prev.sla.thresholds, [field]: value },
      },
    }));
  };

  const updateTriageSla = (field, value) => {
    setSettings(prev => ({
      ...prev,
      sla: {
        ...prev.sla,
        triage: { ...prev.sla.triage, [field]: value },
      },
    }));
  };

  const updateSchedulingSla = (field, value) => {
    setSettings(prev => ({
      ...prev,
      sla: {
        ...prev.sla,
        scheduling: { ...prev.sla.scheduling, [field]: value },
      },
    }));
  };

  const updateBusinessHours = (field, value) => {
    setSettings(prev => ({
      ...prev,
      businessHours: { ...prev.businessHours, [field]: value },
    }));
  };

  const toggleWorkingDay = (dayValue) => {
    setSettings(prev => {
      const days = prev.businessHours.workingDays;
      const next = days.includes(dayValue)
        ? days.filter(d => d !== dayValue)
        : [...days, dayValue].sort((a, b) => a - b);
      return {
        ...prev,
        businessHours: { ...prev.businessHours, workingDays: next },
      };
    });
  };

  // ---- Access check ----
  if (!userIsPlatformAdmin && !userIsTenantAdmin && !userIsOpsManager) {
    return (
      <div className="p-8">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-red-400">Access Denied</h3>
            <p className="text-sm text-red-300/80 mt-1">
              You do not have permission to view Impact Settings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
            <Settings className="w-5 h-5 text-white" />
          </div>
          <div data-learning-target="impact_settings_overview" className={focusHighlight('impact_settings_overview', activeFocusTarget).trim()}>
            <h1 className="text-2xl font-bold text-white">Impact Settings</h1>
            <p className="text-sm text-slate-400">
              {userIsPlatformAdmin
                ? 'Configure capacity values, SLA thresholds, and business hours per tenant'
                : 'Capacity values, SLA thresholds, and business hours for your organization'}
            </p>
          </div>
        </div>
        {readOnly && (
          <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg w-fit">
            <Shield className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-amber-400 font-medium">Read-only — Managed by Tenant Admin</span>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* Tenant Selector (platform_admin) or Tenant Label (tenant_admin / ops_manager) */}
          {showTenantPicker ? (
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Select Tenant
              </label>
              <div className="relative">
                <select
                  value={selectedTenantId}
                  onChange={(e) => setSelectedTenantId(e.target.value)}
                  disabled={loadingSettings || saving}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-emerald-500/50 disabled:opacity-50"
                >
                  {Object.keys(tenants).length === 0 ? (
                    <option value="">No tenants available</option>
                  ) : (
                    <>
                      <option value="">Select a tenant...</option>
                      {Object.values(tenants).map((tenant) => (
                        <option key={tenant.id || tenant.tenantId} value={tenant.id || tenant.tenantId}>
                          {tenant.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
              {selectedTenantId && (
                <p className="text-xs text-slate-500 mt-2">
                  Firestore path: <code className="text-emerald-400">tenants/{selectedTenantId}/settings/impact</code>
                </p>
              )}
            </div>
          ) : (
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 mb-6 flex items-center gap-3">
              <Building className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm font-medium text-slate-300">Your Organization</p>
                <p className="text-xs text-slate-500">Tenant ID: {selectedTenantId}</p>
              </div>
            </div>
          )}

          {/* Build 2P — EHR Integration moved to its own page. Leave a compact
              link card here (platform_admin + tenant_admin only — unchanged from
              who could see the old card). ops_manager never saw the EHR card and
              still doesn't. */}
          {selectedTenantId && (userIsPlatformAdmin || userIsTenantAdmin) && (
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 mb-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-sky-500/15 flex items-center justify-center flex-shrink-0">
                    <Plug className="w-5 h-5 text-sky-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">EHR Integration</h2>
                    <p className="text-xs text-slate-400 mt-1 max-w-md">
                      Configure SMART/FHIR connection, sandbox testing, user mapping, and IT setup guide.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onNavigate && onNavigate('ehr-integration')}
                  disabled={!onNavigate}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
                >
                  Open EHR Integration <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Error/Success/Validation Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {validationErrors.length > 0 && (
            <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-400 mb-1">Please fix the following before saving:</p>
                  <ul className="text-sm text-amber-300/80 list-disc ml-4 space-y-0.5">
                    {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-400">{success}</p>
            </div>
          )}

          {/* Settings Form */}
          {loadingSettings ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
              <span className="ml-3 text-slate-400">Loading settings...</span>
            </div>
          ) : settings ? (
            <div className="space-y-6">

              {/* ================================================================
                  SLA / Stuck Queue Settings
                  ================================================================ */}
              <div data-learning-target="impact_sla_stuck_queue_settings" className={`bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden${focusHighlight('impact_sla_stuck_queue_settings', activeFocusTarget)}`}>
                <div className="p-4 border-b border-slate-700/50 bg-slate-800/40">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-amber-400" />
                    <h2 className="text-lg font-semibold text-white">SLA / Stuck Queue Settings</h2>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Define how long a referral can sit in each state before it&apos;s considered stuck.
                    These thresholds power the Ops Manager &quot;Live Fires&quot; dashboard cards.
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    All SLA calculations use business hours only (configured below).
                  </p>
                </div>

                <div className="p-6 space-y-6">
                  {/* Unified SLA rows */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-3">
                      SLA Hours Per Workflow State
                    </label>
                    <div className="space-y-3">
                      {SLA_ROWS.map((row) => {
                        let value, onChange, borderCls, textCls;
                        if (row.type === 'state') {
                          value = settings.sla.states[row.key]?.slaHours ?? DEFAULT_IMPACT_SETTINGS.sla.states[row.key]?.slaHours;
                          onChange = (e) => updateSlaHours(row.key, parseInt(e.target.value) || 1);
                          borderCls = row.borderClass || 'border-slate-700';
                          textCls = row.textClass || 'text-white';
                        } else if (row.type === 'triage') {
                          value = settings.sla.triage?.[row.field] ?? DEFAULT_IMPACT_SETTINGS.sla.triage[row.field];
                          onChange = (e) => updateTriageSla(row.field, parseInt(e.target.value) || 1);
                          borderCls = row.borderClass || 'border-slate-700';
                          textCls = row.textClass || 'text-white';
                        } else {
                          value = settings.sla.scheduling?.[row.field] ?? DEFAULT_IMPACT_SETTINGS.sla.scheduling[row.field];
                          onChange = (e) => updateSchedulingSla(row.field, parseInt(e.target.value) || 1);
                          borderCls = row.borderClass || 'border-slate-700';
                          textCls = row.textClass || 'text-white';
                        }
                        return (
                          <div key={row.key} className="flex items-center gap-4">
                            <div className="w-48 flex-shrink-0">
                              <div className={`text-sm font-medium ${textCls}`}>{row.label}</div>
                              <div className="text-xs text-slate-500">{row.desc}</div>
                            </div>
                            <input
                              type="number"
                              min="1"
                              max="720"
                              step="1"
                              value={value}
                              onChange={onChange}
                              disabled={saving || readOnly}
                              className={`w-24 px-3 py-2 bg-slate-900/50 border ${borderCls} rounded-lg ${textCls} text-center font-semibold focus:outline-none focus:border-amber-500/50 disabled:opacity-50`}
                            />
                            <span className="text-slate-500 text-sm">hours</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="border-t border-slate-700/50 pt-6">
                    <label className="block text-sm font-medium text-slate-300 mb-3">
                      Warn / Critical Thresholds
                    </label>
                    <p className="text-xs text-slate-500 mb-4 flex items-start gap-2">
                      <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        When the percentage of referrals in a queue that exceed SLA (stuck) surpasses the
                        <strong className="text-amber-400"> Warn</strong> threshold, a warning is shown.
                        Beyond the <strong className="text-red-400"> Critical</strong> threshold, it is considered a live fire.
                      </span>
                    </p>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-medium text-amber-400 mb-1.5">
                          Warn Threshold (% of queue stuck)
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            min="5"
                            max="20"
                            step="1"
                            value={Math.round(settings.sla.thresholds.warnPercent * 100)}
                            onChange={(e) => updateSlaThreshold('warnPercent', (parseInt(e.target.value) || 10) / 100)}
                            disabled={saving || readOnly}
                            className="w-24 px-3 py-2 bg-slate-900/50 border border-amber-500/30 rounded-lg text-amber-300 text-center font-semibold focus:outline-none focus:border-amber-500/50 disabled:opacity-50"
                          />
                          <span className="text-slate-500 text-sm">% of queue stuck</span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">Range: 5% – 20%</p>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-red-400 mb-1.5">
                          Critical Threshold (% of queue stuck)
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            min="15"
                            max="40"
                            step="1"
                            value={Math.round(settings.sla.thresholds.criticalPercent * 100)}
                            onChange={(e) => updateSlaThreshold('criticalPercent', (parseInt(e.target.value) || 25) / 100)}
                            disabled={saving || readOnly}
                            className="w-24 px-3 py-2 bg-slate-900/50 border border-red-500/30 rounded-lg text-red-300 text-center font-semibold focus:outline-none focus:border-red-500/50 disabled:opacity-50"
                          />
                          <span className="text-slate-500 text-sm">% of queue stuck</span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">Range: 15% – 40%</p>
                      </div>
                    </div>
                  </div>

                  {settings.sla.thresholds.source === 'tenant' && (
                    <p className="text-xs text-emerald-500/70 flex items-center gap-1.5">
                      <CheckCircle className="w-3 h-3" />
                      Custom thresholds set by tenant admin
                    </p>
                  )}
                </div>
              </div>

              {/* ================================================================
                  Business Hours for SLA Clock
                  ================================================================ */}
              <div data-learning-target="impact_business_hours_sla_clock" className={`bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden${focusHighlight('impact_business_hours_sla_clock', activeFocusTarget)}`}>
                <div className="p-4 border-b border-slate-700/50 bg-slate-800/40">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-cyan-400" />
                    <h2 className="text-lg font-semibold text-white">Business Hours for SLA Clock</h2>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    SLA elapsed time only counts working hours. Configure when the clock runs.
                  </p>
                </div>

                <div className="p-6 space-y-6">
                  {/* Timezone */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Timezone
                    </label>
                    <div className="relative">
                      <select
                        value={settings.businessHours.timezone}
                        onChange={(e) => updateBusinessHours('timezone', e.target.value)}
                        disabled={saving || readOnly}
                        className="w-72 px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500/50 disabled:opacity-50"
                      >
                        {US_TIMEZONES.map((tz) => (
                          <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Working Days */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Working Days
                    </label>
                    <div className="flex gap-2">
                      {WEEKDAYS.map(({ value, label }) => {
                        const active = settings.businessHours.workingDays.includes(value);
                        return (
                          <button
                            key={value}
                            onClick={() => toggleWorkingDay(value)}
                            disabled={saving || readOnly}
                            className={`w-12 h-10 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                              active
                                ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                                : 'bg-slate-900/40 text-slate-500 border border-slate-700/50 hover:border-slate-600'
                            }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Start / End Hour */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Start Hour
                      </label>
                      <div className="relative">
                        <select
                          value={settings.businessHours.startHour}
                          onChange={(e) => updateBusinessHours('startHour', parseInt(e.target.value))}
                          disabled={saving || readOnly}
                          className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500/50 disabled:opacity-50"
                        >
                          {HOURS.map((h) => (
                            <option key={h.value} value={h.value}>{h.label}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        End Hour
                      </label>
                      <div className="relative">
                        <select
                          value={settings.businessHours.endHour}
                          onChange={(e) => updateBusinessHours('endHour', parseInt(e.target.value))}
                          disabled={saving || readOnly}
                          className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500/50 disabled:opacity-50"
                        >
                          {END_HOURS.map((h) => (
                            <option key={h.value} value={h.value}>{h.label}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 flex items-start gap-2">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      SLA clock counts only working hours. Time outside these hours (evenings, weekends) is excluded.
                      <br />
                      Example: A referral arriving Friday at 4 PM with a 4-hour SLA will not be considered stuck until Monday at 12 PM.
                      <br />
                      All SLA calculations are based on business time, not calendar time.
                    </span>
                  </p>
                </div>
              </div>

              {/* ================================================================
                  Scheduling Availability (booking constraints — separate from SLA)
                  ================================================================ */}
              <SchedulingAvailabilitySection
                tenantId={selectedTenantId}
                readOnly={readOnly}
                currentUserUid={user?.uid}
                businessHours={settings.businessHours}
                activeFocusTarget={activeFocusTarget}
              />

              {/* ================================================================
                  Capacity Value Settings (formerly Impact Assumptions)
                  ================================================================ */}
              <div data-learning-target="impact_capacity_value_settings" className={`bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden${focusHighlight('impact_capacity_value_settings', activeFocusTarget)}`}>
                <div className="p-4 border-b border-slate-700/50 bg-slate-800/40">
                  <div className="flex items-center gap-3">
                    <Calculator className="w-5 h-5 text-emerald-400" />
                    <h2 className="text-lg font-semibold text-white">Capacity Value Settings</h2>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Configure values used to calculate time reclaimed and economic impact
                  </p>
                </div>

                <div className="p-6 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Minutes Saved Per Prevented Referral
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="number"
                        min="0"
                        max="120"
                        step="1"
                        value={settings.assumptions.minutesSavedPerPreventedReferral}
                        onChange={(e) => updateAssumption('minutesSavedPerPreventedReferral', parseInt(e.target.value) || 0)}
                        disabled={saving || readOnly}
                        className="w-32 px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-lg font-semibold focus:outline-none focus:border-emerald-500/50 disabled:opacity-50"
                      />
                      <span className="text-slate-400">minutes</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-2 flex items-start gap-2">
                      <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        Estimated specialist time saved when a referral is stopped at the Gate (NOT_YET) before reaching clinical review.
                        Used to calculate <strong className="text-emerald-400">Estimated Hours Reclaimed</strong>.
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* ================================================================
                  Outcome Definitions
                  ================================================================ */}
              <div data-learning-target="impact_outcome_definitions" className={`bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden${focusHighlight('impact_outcome_definitions', activeFocusTarget)}`}>
                <div className="p-4 border-b border-slate-700/50 bg-slate-800/40">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-400" />
                    <h2 className="text-lg font-semibold text-white">Outcome Definitions</h2>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Which outcomes count as &quot;completed&quot; vs &quot;prevented&quot; for analytics
                  </p>
                </div>

                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Completed Outcomes
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {settings.definitions.completedOutcomeStatuses.map((status) => (
                          <span
                            key={status}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                              status === 'urgent'
                                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            }`}
                          >
                            {status}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Referrals that reached a final decision
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Prevented Outcomes
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {settings.definitions.preventedOutcomeStatuses.map((status) => (
                          <span
                            key={status}
                            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30"
                          >
                            {status}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Referrals intercepted before completion
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ================================================================
                  Action Buttons
                  ================================================================ */}
              <div className="flex items-center justify-between pt-4">
                <button
                  onClick={loadSettings}
                  disabled={saving || loadingSettings}
                  className="flex items-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <RefreshCcw className={`w-4 h-4 ${loadingSettings ? 'animate-spin' : ''}`} />
                  Refresh
                </button>

                {!readOnly && (
                  <button
                    data-learning-target="impact_save_settings"
                    onClick={handleSave}
                    disabled={saving || !hasChanges}
                    className={`flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors${focusHighlight('impact_save_settings', activeFocusTarget)}`}
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Save Settings
                      </>
                    )}
                  </button>
                )}
              </div>

              {!readOnly && hasChanges && (
                <p className="text-xs text-amber-400 text-center">
                  You have unsaved changes
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-16 text-slate-400">
              Select a tenant to configure impact settings
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TenantImpactSettingsView;
